import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from 'react-simple-maps';
import { 
  Globe, Users, MapPin, RefreshCw, Plus, Search, Filter,
  Building2, Phone, Mail, ChevronDown, X, Edit2, Trash2,
  TrendingUp, Package, DollarSign, Info
} from 'lucide-react';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Country name mappings for matching geo data
const countryNameMappings = {
  'USA': 'United States of America',
  'United States': 'United States of America',
  'UK': 'United Kingdom',
  'UNITED KINGDOM': 'United Kingdom',
  'UAE': 'United Arab Emirates',
  'SINGAPORE': 'Singapore',
  'MALAYSIA': 'Malaysia',
  'AUSTRALIA': 'Australia',
  'GREECE': 'Greece',
  'Netherland': 'Netherlands',
  'NETHERLANDS': 'Netherlands',
  'Germany': 'Germany',
  'GERMANY': 'Germany',
  'Belgium': 'Belgium',
  'BELGIUM': 'Belgium',
  'FINLAND': 'Finland',
  'Israel': 'Israel',
  'ISRAEL': 'Israel',
  'Saudi Arabia': 'Saudi Arabia',
  'SAUDI ARABIA': 'Saudi Arabia',
  'Bulgaria': 'Bulgaria',
  'BULGARIA': 'Bulgaria',
  'Cyprus': 'Cyprus',
  'CYPRUS': 'Cyprus',
  'Hong Kong': 'China',
  'HONG KONG': 'China',
  'VIETNAM': 'Vietnam',
  'Vietnam': 'Vietnam',
  'CANADA': 'Canada',
  'Canada': 'Canada',
  'FRANCE': 'France',
  'France': 'France',
};

// Color scale for supplier density
const getCountryColor = (count) => {
  if (count === 0) return '#E2E8F0'; // Gray - no suppliers
  if (count === 1) return '#BFDBFE'; // Light blue
  if (count === 2) return '#93C5FD'; // Blue
  if (count <= 4) return '#60A5FA'; // Medium blue
  if (count <= 7) return '#3B82F6'; // Bright blue
  if (count <= 10) return '#2563EB'; // Dark blue
  return '#1D4ED8'; // Very dark blue for 10+
};

// Country coordinates for mapping
const countryCoordinates = {
  'India': [78.9629, 20.5937],
  'USA': [-95.7129, 37.0902],
  'United States': [-95.7129, 37.0902],
  'China': [104.1954, 35.8617],
  'UAE': [53.8478, 23.4241],
  'United Arab Emirates': [53.8478, 23.4241],
  'Dubai': [55.2708, 25.2048],
  'Saudi Arabia': [45.0792, 23.8859],
  'Germany': [10.4515, 51.1657],
  'UK': [-3.4360, 55.3781],
  'United Kingdom': [-3.4360, 55.3781],
  'Japan': [138.2529, 36.2048],
  'South Korea': [127.7669, 35.9078],
  'Australia': [133.7751, -25.2744],
  'Singapore': [103.8198, 1.3521],
  'Malaysia': [101.9758, 4.2105],
  'Thailand': [100.9925, 15.8700],
  'Vietnam': [108.2772, 14.0583],
  'Indonesia': [113.9213, -0.7893],
  'Netherlands': [5.2913, 52.1326],
  'Belgium': [4.4699, 50.5039],
  'France': [2.2137, 46.2276],
  'Italy': [12.5674, 41.8719],
  'Spain': [-3.7492, 40.4637],
  'Canada': [-106.3468, 56.1304],
  'Mexico': [-102.5528, 23.6345],
  'Brazil': [-51.9253, -14.2350],
  'Russia': [105.3188, 61.5240],
  'Turkey': [35.2433, 38.9637],
  'Egypt': [30.8025, 26.8206],
  'South Africa': [22.9375, -30.5595],
  'Nigeria': [8.6753, 9.0820],
  'Kenya': [37.9062, -0.0236],
  'Pakistan': [69.3451, 30.3753],
  'Bangladesh': [90.3563, 23.6850],
  'Sri Lanka': [80.7718, 7.8731],
  'Nepal': [84.1240, 28.3949],
  'Oman': [55.9754, 21.4735],
  'Qatar': [51.1839, 25.3548],
  'Kuwait': [47.4818, 29.3117],
  'Bahrain': [50.5577, 26.0667],
  'Iran': [53.6880, 32.4279],
  'Iraq': [43.6793, 33.2232],
};

// Indian state coordinates
const stateCoordinates = {
  'Maharashtra': [75.7139, 19.7515],
  'Gujarat': [71.1924, 22.2587],
  'Tamil Nadu': [78.6569, 11.1271],
  'Karnataka': [75.7139, 15.3173],
  'Delhi': [77.1025, 28.7041],
  'Uttar Pradesh': [80.9462, 26.8467],
  'West Bengal': [87.8550, 22.9868],
  'Rajasthan': [74.2179, 27.0238],
  'Madhya Pradesh': [78.6569, 22.9734],
  'Andhra Pradesh': [79.7400, 15.9129],
  'Telangana': [79.0193, 18.1124],
  'Kerala': [76.2711, 10.8505],
  'Punjab': [75.3412, 31.1471],
  'Haryana': [76.0856, 29.0588],
  'Bihar': [85.3131, 25.0961],
  'Odisha': [85.0985, 20.9517],
  'Jharkhand': [85.2799, 23.6102],
  'Chhattisgarh': [81.8661, 21.2787],
  'Assam': [92.9376, 26.2006],
  'Goa': [74.1240, 15.2993],
};

export default function SupplierDashboard() {
  const { API } = useContext(AppContext);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [mapPosition, setMapPosition] = useState({ coordinates: [78, 22], zoom: 1 });
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Calculate supplier count by country for map coloring
  const suppliersByCountry = useMemo(() => {
    const counts = {};
    suppliers.forEach(s => {
      const country = s.country || 'Unknown';
      // Normalize country name for matching
      const normalizedCountry = countryNameMappings[country] || country;
      counts[normalizedCountry] = (counts[normalizedCountry] || 0) + 1;
    });
    return counts;
  }, [suppliers]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/master-data/raw-material/suppliers`);
      setSuppliers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
    setLoading(false);
  };

  const syncFromSheets = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/sync/master-data/raw-material/suppliers`, {});
      await fetchSuppliers();
    } catch (error) {
      console.error('Sync error:', error);
    }
    setSyncing(false);
  };

  // Get marker coordinates for a supplier
  const getSupplierCoordinates = (supplier) => {
    // First check if supplier has lat/long
    if (supplier.latitude && supplier.longitude) {
      return [parseFloat(supplier.longitude), parseFloat(supplier.latitude)];
    }
    
    // Check state (for Indian suppliers)
    if (supplier.state && stateCoordinates[supplier.state]) {
      return stateCoordinates[supplier.state];
    }
    
    // Check country
    if (supplier.country && countryCoordinates[supplier.country]) {
      return countryCoordinates[supplier.country];
    }
    
    // Default to India
    return countryCoordinates['India'];
  };

  // Get unique countries for filter
  const countries = ['all', ...new Set(suppliers.map(s => s.country || 'India').filter(Boolean))];

  // Filter suppliers
  const filteredSuppliers = suppliers.filter(s => {
    const matchesSearch = !searchTerm || 
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCountry = selectedCountry === 'all' || (s.country || 'India') === selectedCountry;
    return matchesSearch && matchesCountry;
  });

  // Get markers for map
  const markers = filteredSuppliers.map(s => ({
    ...s,
    coordinates: getSupplierCoordinates(s)
  }));

  // Stats
  const stats = {
    total: suppliers.length,
    active: suppliers.filter(s => s.status === 'Active').length,
    countries: new Set(suppliers.map(s => s.country || 'India')).size,
    cities: new Set(suppliers.map(s => s.city).filter(Boolean)).size,
  };

  return (
    <div className="p-6 space-y-6" data-testid="supplier-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="text-blue-600" size={28} />
            Supplier Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Global supplier network for raw materials</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={syncFromSheets}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            data-testid="sync-suppliers-btn"
          >
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync from Sheets'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Suppliers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-xs text-gray-500">Active Suppliers</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Globe size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.countries}</p>
              <p className="text-xs text-gray-500">Countries</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <MapPin size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.cities}</p>
              <p className="text-xs text-gray-500">Cities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search suppliers by name, code, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
              data-testid="supplier-search"
            />
          </div>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100"
            data-testid="country-filter"
          >
            <option value="all">All Countries</option>
            {countries.filter(c => c !== 'all').map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Full Width Map Section */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-between">
          <h2 className="font-bold text-white flex items-center gap-2 text-lg">
            <Globe size={22} className="text-blue-200" />
            Global Supplier Network
          </h2>
          <div className="flex items-center gap-4">
            {hoveredCountry && (
              <span className="text-sm bg-white/20 backdrop-blur text-white px-4 py-1.5 rounded-full font-medium">
                {hoveredCountry.name}: {hoveredCountry.count} supplier{hoveredCountry.count !== 1 ? 's' : ''}
              </span>
            )}
            <span className="text-sm text-blue-100">{markers.length} suppliers worldwide</span>
          </div>
        </div>
        <div className="h-[600px] bg-gradient-to-b from-blue-50 to-slate-100 relative">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 150,
              center: [20, 20]
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <ZoomableGroup
              center={mapPosition.coordinates}
              zoom={mapPosition.zoom}
              onMoveEnd={({ coordinates, zoom }) => setMapPosition({ coordinates, zoom })}
              minZoom={1}
              maxZoom={8}
            >
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const countryName = geo.properties.name;
                    const supplierCount = suppliersByCountry[countryName] || 0;
                    const fillColor = getCountryColor(supplierCount);
                    
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fillColor}
                        stroke="#fff"
                        strokeWidth={0.5}
                        onMouseEnter={() => {
                          if (supplierCount > 0) {
                            setHoveredCountry({ name: countryName, count: supplierCount });
                          }
                        }}
                        onMouseLeave={() => setHoveredCountry(null)}
                        onClick={() => {
                          if (supplierCount > 0) {
                            const matchingCountry = countries.find(c => 
                              countryNameMappings[c] === countryName || c === countryName
                            );
                            if (matchingCountry) {
                              setSelectedCountry(matchingCountry);
                            }
                          }
                        }}
                        style={{
                          default: { 
                            outline: 'none',
                            transition: 'all 0.3s ease'
                          },
                          hover: { 
                            fill: supplierCount > 0 ? '#F59E0B' : '#CBD5E1',
                            outline: 'none',
                            cursor: supplierCount > 0 ? 'pointer' : 'default',
                            filter: supplierCount > 0 ? 'brightness(1.1) drop-shadow(0 4px 6px rgba(0,0,0,0.1))' : 'none'
                          },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {markers.map((supplier, idx) => (
                <Marker
                  key={supplier.id || idx}
                  coordinates={supplier.coordinates}
                  onClick={() => setSelectedSupplier(supplier)}
                >
                  <g style={{ cursor: 'pointer' }} className="supplier-marker">
                    <circle
                      r={10}
                      fill={supplier.status === 'Active' ? '#10B981' : supplier.status === 'Blacklisted' ? '#EF4444' : '#6B7280'}
                      stroke="#fff"
                      strokeWidth={3}
                      opacity={0.9}
                    />
                    <circle
                      r={4}
                      fill="#fff"
                    />
                  </g>
                  <title>{supplier.name} ({supplier.country})</title>
                </Marker>
              ))}
            </ZoomableGroup>
          </ComposableMap>
          
          {/* Floating Legend */}
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-xl shadow-lg p-4 border border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">Supplier Density</p>
            <div className="flex items-center gap-1 mb-3">
              <div className="w-6 h-4 rounded" style={{backgroundColor: '#E2E8F0'}}></div>
              <div className="w-6 h-4 rounded" style={{backgroundColor: '#BFDBFE'}}></div>
              <div className="w-6 h-4 rounded" style={{backgroundColor: '#60A5FA'}}></div>
              <div className="w-6 h-4 rounded" style={{backgroundColor: '#2563EB'}}></div>
              <div className="w-6 h-4 rounded" style={{backgroundColor: '#1D4ED8'}}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mb-3">
              <span>0</span>
              <span>5</span>
              <span>10+</span>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-xs text-gray-600">Active</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                <span className="text-xs text-gray-600">Inactive</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-xs text-gray-600">Blocked</span>
              </div>
            </div>
          </div>
          
          {/* Zoom Controls */}
          <div className="absolute top-4 right-4 flex flex-col gap-2">
            <button 
              onClick={() => setMapPosition(prev => ({ ...prev, zoom: Math.min(prev.zoom * 1.5, 8) }))}
              className="w-10 h-10 bg-white rounded-lg shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition font-bold text-xl"
            >
              +
            </button>
            <button 
              onClick={() => setMapPosition(prev => ({ ...prev, zoom: Math.max(prev.zoom / 1.5, 1) }))}
              className="w-10 h-10 bg-white rounded-lg shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition font-bold text-xl"
            >
              −
            </button>
            <button 
              onClick={() => setMapPosition({ coordinates: [20, 20], zoom: 1 })}
              className="w-10 h-10 bg-white rounded-lg shadow-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition"
              title="Reset view"
            >
              <Globe size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Supplier List - Below Map */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-gray-700 to-gray-800 flex items-center justify-between">
          <h2 className="font-bold text-white flex items-center gap-2">
            <Users size={20} className="text-gray-300" />
            Supplier Directory
            {selectedCountry !== 'all' && (
              <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm">
                {selectedCountry}
                <button onClick={() => setSelectedCountry('all')} className="ml-2 hover:text-red-300">×</button>
              </span>
            )}
          </h2>
          <span className="text-gray-300 text-sm">{filteredSuppliers.length} suppliers</span>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
              Loading suppliers...
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users size={32} className="mx-auto mb-2 text-gray-300" />
              <p>No suppliers found</p>
              <p className="text-sm mt-1">Sync from Google Sheets to add suppliers</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredSuppliers.map((supplier, idx) => (
                <div
                  key={supplier.id || idx}
                  onClick={() => setSelectedSupplier(supplier)}
                  className={`p-4 rounded-xl border-2 hover:border-blue-400 hover:shadow-md cursor-pointer transition-all ${
                    selectedSupplier?.id === supplier.id ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-100 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight">{supplier.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      supplier.status === 'Active' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : supplier.status === 'Blacklisted'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {supplier.status || 'Active'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{supplier.code}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <MapPin size={12} className="text-blue-500" />
                    <span>{supplier.city || supplier.country || 'Unknown'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected Supplier Modal */}
      {selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSupplier(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Supplier Details</h2>
              <button onClick={() => setSelectedSupplier(null)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building2 size={32} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedSupplier.name}</h3>
                  <p className="text-gray-500">{selectedSupplier.code}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">GST Number</p>
                  <p className="font-medium">{selectedSupplier.gst || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <span className={`px-2 py-0.5 rounded-full text-sm ${
                    selectedSupplier.status === 'Active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {selectedSupplier.status || 'Active'}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Contact</p>
                  <p className="font-medium">{selectedSupplier.contact || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Location</p>
                  <p className="font-medium">
                    {[selectedSupplier.city, selectedSupplier.state, selectedSupplier.country].filter(Boolean).join(', ') || 'India'}
                  </p>
                </div>
              </div>
              
              {selectedSupplier.address && (
                <div className="pt-2">
                  <p className="text-xs text-gray-500 mb-1">Address</p>
                  <p className="text-gray-700">{selectedSupplier.address}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
